<template lang='pug'>
  campaign-layout
    v-container(
      dark
      fluid
      grid-list-xl
    )
      v-snackbar(v-model="getPublishedCampaignIsSuccess" color="success")
        div {{ publishedCampaignSuccessMessage }}
      v-snackbar(v-model="getPublishedCampaignHasError" color="error")
        div {{ publishedCampaignErrorMessage }}
      div.copy-box(v-if="hasBulkTextsAccess") Ensure all of your contacts have provided consent prior to texting them. You can learn more about SMS consent best practices and tips 
        a.font-weight-bold.accent--text(
          href='https://lonewolf.my.site.com/s/article/LD-Best-practices-for-texting-in-LionDesk',
          target='_blank'
        ) here.
      v-flex(xs12)
        action-title-bar(:description="badgeText" title="Campaigns" data-cy="title-bar")
          v-btn.ml-0.primary(
            @click.native="handleAddResourceModal"
            data-cy="campaigns.create"
            flat
          )
            | Create Campaign
          v-tooltip(top)
            v-btn.mr-0.icon-container(
              v-if="shouldShowBulkDelete"
              icon 
              data-cy="bulk-delete" 
              slot="activator"
              @click="handleRemoveResourceModal('delete', 'multiple')"
            )
              v-icon delete
            span Delete Campaigns
          template(slot="extras")
            v-layout(row)
              v-flex
                v-text-field(
                  solo
                  hide-details
                  single-line
                  prepend-inner-icon='search'
                  v-model='search'
                  label='Search'
                )
      v-layout(align-space-between justify-space-between fill-height row)
        v-flex
          v-card
            v-data-table.lonewolf-table(
              ref="campaignsTable"
              :headers="headers"
              :items="campaigns"
              header-text="text"
              header-key="value"
              disable-initial-sort
              data-test-id="data-table"
              :class="{ 'in-progress': isLoading }"
              :loading="isLoading"
              hide-actions
              :no-data-text="noDataMessage"
              item-key="id"
              v-model="selected"
            )
              template(slot="headers" slot-scope="headers")
                list-header(
                  data-test-id="data-table-header"
                  :headers="headers"
                  :selected="selected"
                  :page-limit="paginationGetPageLimit"
                  :is-viewing-last-page="isViewingLastPage"
                  :campaigns="campaigns"
                  :sort-field="paginationGetSortField"
                  :sort-order="paginationGetSortOrder"
                  @click:sort="paginationSetSort"
                  @click:select-all="selectAll"
                )
              template(slot="items" slot-scope="data")
                list-data(
                  data-test-id="data-table-data"
                  :data="data"
                  :selected="selected"
                  :actions="actions"
                  :use-data-table-menu="false"
                  :is-admin="broker && broker.is_admin"
                  is-campaign-list
                  @click:remove="handleRemoveResourceModal('delete', 'single', data.item.id)"
                  @click:edit="redirectToShow(data.item.id)"
                  @click:clone="handleCloneList(data.item.id)"
                  @click:select-one="selectOne"
                  @click:publish="handlePublishCampaignModal(data.item.id)"
                  @click:unpublish="handleUnpublishCampaign(data.item.id_drip_shared)"
                  @click:share="handleShareCampaignModal(data.item.id)"
                  @click:unshare="handleUnshareCampaign(data.item.id)"
                  @click:edit-published="handleEditPublishedCampaign(data.item.id_drip_shared)"
                  @click:stop="openStopCampaignModal"
                  @openActiveContactsList="openActiveContactsList(data.item.id)"
                )
                  // TODO - Commenting this out as we are currently not using
                  // the ellipses menu on the campaigns page, if we need it again
                  // we can simply uncomment this and add an extra header column
                  //- template(slot="actionMenu")
                  //-   td
                  //-     v-menu.action-menu(offset-y)
                  //-       v-icon(color="grey" slot="activator") more_vert
                  //-       v-list
                  //-         v-list-tile(@click="redirectToShow(data.item.id)")
                  //-           v-list-tile-action
                  //-             v-icon edit
                  //-           v-list-tile-content
                  //-           | Edit Campaign
                  //-         v-list-tile(data-cy="campaigns.remove" @click="handleRemoveResourceModal('delete', 'single', data.item.id)")
                  //-           v-list-tile-action
                  //-             v-icon delete
                  //-           v-list-tile-content
                  //-           | Delete Campaign
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  :value="paginationGetPage"
                  :length="paginationPageCount(totals)"
                  @input="setPage"
                  :total-visible="7"
                  v-show="paginationPageCount(totals) > 1"
                )

              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="paginationGetPageLimit"
                  @change:limit='paginationSetPaginationLimit'
                  )
    remove-resource-modal(
      v-model="removeBulkActionDialog"
      :action="removeAction"
      resource-singular="campaign"
      resource-plural="campaigns"
      single-amount-message="Are you sure you want to delete this campaign? This action cannot be undone."
      :multiple-amount-message="removeMultipleDialog"
      :remove-amount="removeAmount"
      @close="onRemoveBulkResourceModalClose"
    )
    add-resource-modal(v-model="addCampaignDialog" title='Campaign Name' resource-singular="campaign" @close="onAddResourceModalClose")
    active-contacts-modal(
      v-model="showActiveContactsDialog"
      @close="closeActiveContactsDialog"
      :campaignId="modalCampaignId"
      :contacts="contacts"
      :total="contactsTotal"
      :limit="contactsLimit"
      :skip="contactsSkip"
      @setPage="setContactsPage"
      @setPageLimit="setContactsPageLimit"
        )
    publish-campaign-modal(
      v-model="publishCampaignDialog"
      :publishedCampaignData="publishedCampaign"
      @close="onPublishCampaignModalClose"
      @on-edit="onEditPublishedCampaignModalClose"
    )
    share-campaign-modal(v-model="shareCampaignDialog" @close="onShareCampaignModalClose")
    clone-campaign-modal(
      v-model="cloneCampaignDialog"
      @close="onCloseCloneCampaignModal"
      @ok="onOkCloneCampaignModal"
    )
    stop-contacts-modal(
      v-model="showStopContactsModal"
      :contacts="contacts"
      :total="contactsTotal"
      :limit="contactsLimit"
      :skip="contactsSkip"
      @close="showStopContactsModal = false"
      @setPage="setContactsPage"
      @setPageLimit="setContactsPageLimit"
      @stop="handleStopCampaign"
    )
    stop-campaign-modal(
      v-model="showStopCampaignDialog"
      :amount="stopCampaignAmount"
      @cancel="closeStopCampaignDialog"
      @stop="confirmStopCampaign"
    )
    bulk-actions-in-progress-modal(ref="bulkActionsInProgressModal")
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import AddResourceModal from '@/Core/components/AddResourceModal';
import Pagination from '@/Core/mixins/pagination';
import Search from '@/Core/mixins/search';
import ShowItemTotals from '@/Core/mixins/show-item-totals';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import { debounce } from '@/Core/utilities';
import PaginationLimit from '@/Core/components/PaginationLimit';
import RemoveResourceModal from '@/Tasks/components/RemoveResourceModal';
import PublishCampaignModal from './components/PublishCampaignModal';
import CloneCampaignModal from './components/CloneCampaignModal';
import ShareCampaignModal from './components/ShareCampaignModal';
import { LdGatedSwitch, LdFeatures } from 'lion-vue';
import CampaignLayout from './Layout';
import ListData from './components/CampaignsListData';
import ListHeader from './components/CampaignsListHeader';
import { leadAssistAuthUrl } from '@/Campaigns/utilities';
import ActiveContactsModal from './components/ActiveContactsModal'
import StopContactsModal from './components/StopContactsModal';
import StopCampaignModal from './components/StopCampaignModal';
import BulkActionsInProgressModal from '../Contacts/components/bulk-actions-report/BulkActionsInProgressModal';

export default {
  components: {
    CampaignLayout,
    ActionTitleBar,
    AddResourceModal,
    PublishCampaignModal,
    CloneCampaignModal,
    ShareCampaignModal,
    ListData,
    ListHeader,
    PaginationLimit,
    RemoveResourceModal,
    LdGatedSwitch,
    ActiveContactsModal,
    StopContactsModal,
    StopCampaignModal,
    BulkActionsInProgressModal,
  },
  mixins: [
    Search,
    Pagination,
    ShowItemTotals,
  ],
  beforeDestroy() {
    this.resetMessageState();
  },
  data() {
    return {
      LdFeatures,
      addCampaignDialog: false,
      showActiveContactsDialog: false,
      showStopContactsModal: false,
      showStopCampaignDialog: false,
      modalCampaignId: 0,
      paginationSortOrder: 'desc',
      paginationSortField: 'modified_at',
      removeAction: 'delete',
      removeAmount: 'single',
      expanded: [],
      selected: [],
      actions: [],
      publishedCampaignToUpdate: null,
      publishCampaignDialog: false,
      shareCampaignDialog: false,
      cloneCampaignDialog: false,
      campaignPayloadToClone: null,
      dripSharedId: 0,
      /**
       * used when toggling lead assist
       * if true, will show message until the request resolves
       */
      isWaitingForLeadAssist: false,
      noDataMessage: 'No campaigns were found. Click “add campaign” to create a new campaign.',
      stopCampaignPayload: null,
      stopCampaignAmount: null,
      removeBulkActionDialog: false,
      removeCampaignId: null,
    };
  },
  computed: {
    ...mapGetters({
      campaigns: 'campaigns/campaigns',
      headers: 'campaigns/headers',
      hasAccessToFeature: 'userInformation/hasAccessToFeature',
    }),
    ...mapState({
      userInformation: state => state.userInformation.user,
      newCampaign: state => state.campaigns.newCampaign,
      totals: state => state.campaigns.totals,
      isLoading: state => state.campaigns.isLoading,
      hasPublishedCampaignError: state => state.publishedCampaigns.hasError,
      publishedCampaignErrorMessage: state => state.publishedCampaigns.errorMessage,
      isPublishedCampaignSuccess: state => state.publishedCampaigns.isSuccess,
      publishedCampaignSuccessMessage: state => state.publishedCampaigns.successMessage,
      publishedCampaign: state => state.publishedCampaigns.campaign,
      broker: state => state.contact.broker,
      contacts: state => state.campaignContacts.contacts,
      contactsTotal: state => state.campaignContacts.total,
      contactsLimit: state => state.campaignContacts.limit,
      contactsSkip: state => state.campaignContacts.skip,
      selectedCampaignId: state => state.campaignContacts.campaignId,
    }),
    isLeadAssistOn() {
      // This is determined by the presence of a "structurely_forwarding_email".
      // This field should be empty in the DB until the user fills in the structerly form.
      // https://accounts.structurely.com/#/integrations/liondesk
      // Once the form is submited, Structurely will generate an email which will be
      // saved in the DB.
      // When the switch is turned off this value gets deleted from the DB.
      return this.userInformation && !!this.userInformation.structurely_forwarding_email;
    },
    badgeText() {
      const numSelected = this.selected.length;
      return this.showItemTotals_show(numSelected, this.totals, 'Campaign', 'Campaigns');
    },
    isViewingLastPage() {
      return this.paginationGetPage === this.paginationPageCount(this.totals);
    },
    shouldShowBulkDelete(){
      return this.selected.length > 1;
    },
    leadAssistAuthUrl,
    getPublishedCampaignHasError: {
      get() {
        return this.hasPublishedCampaignError;
      },
      set(value) {
        this.resetMessageState();
      }
    },
    getPublishedCampaignIsSuccess: {
      get() {
        return this.isPublishedCampaignSuccess;
      },
      set(value) {
        this.resetMessageState();
      }
    },
    affectedContacts() {
      return this.selected.reduce((acc, campaign) => acc += campaign.contacts_with_pending_tasks, 0);
    },
    removeMultipleDialog() {
      let message = "Are you sure you want to delete these campaigns?";
      if (this.affectedContacts) {
        return `This will cancel all the remaining scheduled campaign actions (email, text and task) immediately for ${this.affectedContacts} contact(s). This action cannot be undone.

${message}`;
      }
      return message;
    },
    selectedIds() {
      return this.selected.map(campaign => campaign.id);
    },
    hasBulkTextsAccess() {
      return this.hasAccessToFeature(LdFeatures.BulkTexts);
    },
  },
  async mounted() {
    this.prepareRequest(this.$route.query, this.findCampaigns);
    this.setRouteWatcher(this.findCampaigns);
    this.findBroker();
  },
  watch: {
    search(value) {
      debounce(this.setSearch, [value]);
    },
  },
  methods: {
    ...mapActions({
      getUser: 'userInformation/getUser',
      storeEditCampaign: 'campaigns/editCampaign',
      storeCreateCampaign: 'campaigns/createCampaign',
      findCampaigns: 'campaigns/findCampaigns',
      getOnePublishedCampaign: 'publishedCampaigns/getOnePublishedCampaign',
      createPublishedCampaign: 'publishedCampaigns/createPublishedCampaign',
      removePublishedCampaign: 'publishedCampaigns/removePublishedCampaign',
      disconnectLeadAssist: 'campaigns/disconnect',
      editCampaign: 'campaigns/editCampaign',
      cloneCampaign: 'campaigns/cloneCampaign',
      resetMessageState: 'publishedCampaigns/resetMessageState',
      updatePublishedCampaign: 'publishedCampaigns/updatePublishedCampaign',
      resetPublishedCampaignState: 'publishedCampaigns/resetPublishedCampaignState',
      findBroker: 'contact/findBroker',
      stopCampaign: 'campaigns/stopCampaign',
      setBulkActionType: 'contactsTable/bulkActions/setBulkActionType',
      bulkDeleteCampaigns: 'campaigns/bulkDeleteCampaigns'
    }),
    ...mapActions('campaignContacts', ['getContacts']),
    ...mapMutations('campaignContacts', ['setCampaignId', 'setDefaultState', 'setSkip', 'setLimit']),
    async toggleLeadAssist() {
      this.isWaitingForLeadAssist = true;
      if (this.isLeadAssistOn) {
        await this.disconnectLeadAssist();
        this.isWaitingForLeadAssist = false;
        return;
      }
      window.open(this.leadAssistAuthUrl, '_self');
      await this.getUser();
      this.isWaitingForLeadAssist = false;
    },
    async onRemoveBulkResourceModalClose(isResourceRemoved) {
      this.removeBulkActionDialog = false;
      if (!isResourceRemoved) return;

      const ids = this.removeAmount === 'single' ? [this.removeCampaignId] : this.selectedIds;
      const payload = {
        campaign_ids: ids,
      };
      await this.bulkDeleteCampaigns(payload);

      this.prepareRequest(this.$route.query, this.findCampaigns);
    },
    async onPublishCampaignModalClose(isResourcePublished, data) {
      this.publishCampaignDialog = false;
      this.resetPublishedCampaignState();
      if (isResourcePublished) {
        data["campaign_id"] = this.publishCampaignId;
        await this.publishCampaign(data);
      }
    },
    async onEditPublishedCampaignModalClose(data) {
      this.resetPublishedCampaignState();
      this.publishCampaignDialog = false;
      await this.updatePublishedCampaign(data);
      this.prepareRequest(this.$route.query, this.findCampaigns);
    },
    async onShareCampaignModalClose(isResourceShared) {
      this.shareCampaignDialog = false;
      if (isResourceShared) {
        this.editShareCampaign(this.shareCampaignId);
      }
    },
    onCloseCloneCampaignModal() {
      this.campaignPayloadToClone = null;
      this.cloneCampaignDialog = false;
    },
    onOkCloneCampaignModal() {
      if (!this.campaignPayloadToClone) {
        return;
      }
      const query = this.buildQuery(this.$route.query);
      this.cloneCampaign({ ...this.campaignPayloadToClone, query });
      this.campaignPayloadToClone = null;
      this.cloneCampaignDialog = false;
    },
    redirectToShow(id) {
      this.$router.push({ name: 'campaigns-show', params: { id } });
    },
    handleRemoveResourceModal(action, amount, id = null) {
      this.removeBulkActionDialog = true;
      this.removeCampaignId = id;
      this.removeAction = action;
      this.removeAmount = amount;
    },
    handleAddResourceModal() {
      this.addCampaignDialog = true;
    },
    handlePublishCampaignModal(id = null) {
      this.resetPublishedCampaignState();
      this.publishCampaignDialog = true;
      this.publishCampaignId = id;
    },
    async handleUnpublishCampaign(id = null) {
      await this.removePublishedCampaign(id);
      this.prepareRequest(this.$route.query, this.findCampaigns);
    },
    async handleEditPublishedCampaign(id = null) {
      await this.getOnePublishedCampaign(id);
      this.publishCampaignDialog = true;
    },
    handleShareCampaignModal(id = null) {
      this.shareCampaignDialog = true;
      this.shareCampaignId = id;
    },
    handleUnshareCampaign(id) {
      this.editShareCampaign(id, false);
    },
    handleCloneList(data) {
      this.campaignPayloadToClone = { id: data };
      this.cloneCampaignDialog = true;
    },
    async onAddResourceModalClose(resourceName, resourceSubmitted) {
      if (resourceSubmitted) {
        this.storeCreateCampaign(resourceName).then(res => {
          this.addCampaignDialog = false;
          this.$router.push({ name: 'campaigns-show', params: { id: res.id } });
        }).catch(error => {
          this.submitHasError = true;
          this.submitError = error;
        });
      }
    },
    expand(data) {
      data.expanded = !data.expanded;
    },
    selectOne(data) {
      data.selected = !data.selected;
    },
    /**
     *
     * Rather complicate logic for something that should be quite simple. There are two cases,
     * 1) We are not on the last page (or only page), and if selected.length === pageLimit then
     *    deSelectAll
     * 2) We are on the last page, in which case when selected.length === the number of rows,
     *    we call deSelectAll
     */
    selectAll() {
      if ((!this.isViewingLastPage && this.selected.length === this.paginationGetPageLimit)
        || (this.isViewingLastPage && this.selected.length === this.campaigns.length)) {
        this.deSelectAll();
      } else {
        this.selected = this.campaigns.slice(0, this.paginationGetPageLimit);
      }
    },
    deSelectAll() {
      this.selected = [];
    },
    async publishCampaign(data) {
      await this.createPublishedCampaign(data);
      this.prepareRequest(this.$route.query, this.findCampaigns);
    },
    editShareCampaign(id, share = true) {
      const payload = {
        id,
        is_broker_sharing_on: share,
      };
      this.editCampaign(payload);
    },
    /**
     * Builds the query object, this can potentially be moved to a mixin,
     * but it unfortunately relies on the pagination mixin.
     * @param  {Object} query  the $route query object
     * @return {Object}        the built query object
     */
    buildQuery(query) {
      const newQuery = {
        $skip: (this.paginationGetPage - 1) * this.paginationGetPageLimit,
        $limit: this.paginationGetPageLimit,
        $sort: {},
      };

      const likeSearchString = this.$_search_prepareKeywords(query);
      newQuery.name = {
        $like: `%${likeSearchString}%`,
      };

      const preparedSort = this.$_pagination_prepareSort(query);
      if (preparedSort.modified_at) {
        preparedSort.created_at = preparedSort.modified_at;
      }
      newQuery.$sort = preparedSort;

      return newQuery;
    },
    /**
     * - Reset the select boxes
     * - Build the query to be passed into the API request
     * - Callback with the query params
     *
     * @param {Object} query current $route.query
     * @param {Function} callback API request function once query is built
     */
    prepareRequest(query, callback) {
      this.deSelectAll();
      const newQuery = this.buildQuery(query);
      if (typeof callback !== 'function') {
        throw new this.PaginationError('Callback is not a function');
      }
      callback(newQuery);
    },

    /**
     * Watches for updates and current query and calls API request on change.
     *
     * @param {Function} callback API Request function
     */
    setRouteWatcher(callback) {
      this.$watch('$route.query', query => {
        this.prepareRequest(query, callback);
      });
    },
    async openActiveContactsList(id) {
      await this.setDefaultState()
      await this.setCampaignId(id)
      this.getContacts();
      this.showActiveContactsDialog = true;
    },
    closeActiveContactsDialog() {
      this.showActiveContactsDialog = false;
    },
    setContactsPage(page) {
      this.setSkip((page - 1) * this.contactsLimit);
      this.getContacts();
    },
    setContactsPageLimit(limit) {
      this.setLimit(limit);
      this.setContactsPage(1);
    },
    async openStopCampaignModal(campaignId) {
      await this.setDefaultState();
      await this.setCampaignId(campaignId);
      this.getContacts();
      this.showStopContactsModal = true;
    },
    handleStopCampaign(payload, amount, areAllContactsSelected) {
      payload["campaign_id"] = this.selectedCampaignId;

      this.stopCampaignAmount = areAllContactsSelected ? 'all' : amount.toString();
      this.stopCampaignPayload = payload;
      this.showStopContactsModal = false;
      this.showStopCampaignDialog = true;
    },
    closeStopCampaignDialog() {
      this.showStopCampaignDialog = false;
      this.stopCampaignAmount = null;
      this.stopCampaignPayload = null;
    },
    async confirmStopCampaign() {
      await this.stopCampaign(this.stopCampaignPayload);
      this.closeStopCampaignDialog();
      this.setBulkActionType({type: 'stop', resource: 'campaign'});
      this.$refs.bulkActionsInProgressModal.show();
    },
    setPage(event) {
      this.pagination_scrollToTop(this.$refs.campaignsTable);
      this.paginationSetPage(event);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-btn.icon-container {
    opacity: 0.5;
    &:hover {
      opacity: 1;
    }
}
</style>
